type HighlightResult = {
  fullyHighlighted?: boolean
  matchLevel?: string
  matchedWords?: string[]
  value?: string
}

/**
 * It takes a value, a query, and a tag name, and returns the value with the tag name and query removed
 * @param value - The value of the result.
 * @param query - The search query
 * @param tagName - The tag name of the result. For example, if the result is a link, the tag name is
 * "a".
 * @returns the value of the query parameter.
 */
function sanitizeResultNameForValue(value, query, tagName) {
  const regex = new RegExp(`<${tagName}\\s*.*>\\s*.*<\\/${tagName}>`, 'g')
  const replacedValue = value?.replace(regex, query)

  return (
    replacedValue?.substring(replacedValue?.indexOf(query) + query?.length) ??
    null
  )
}

/**
 * It takes the result from Algolia's autocomplete API, the query that was typed in, and the HTML tag
 * that will be used to wrap the matched text. It then returns the matched text in lowercase
 * @param {any} result - The result object from Algolia
 * @param {string} query - The query that the user typed in the search bar.
 * @param {string} htmlTag - The HTML tag that will be used to wrap the matched text.
 * @returns A string
 */
export default function getAlgoliaAutocompleteResult(
  result: any,
  query: string,
  htmlTag: string,
): string {
  const highlightResult = result?._highlightResult ?? {}

  /* Filtering the result object from Algolia's autocomplete API to find the value that
  matches the query. */
  const matchedValue: HighlightResult[] =
    Object.values(highlightResult)
      ?.filter((item: HighlightResult) => {
        const itemMatchLevel = item?.matchLevel || item?.[0]?.matchLevel

        return itemMatchLevel === 'full'
      })
      ?.flat() ?? null

  const futureSentence =
    sanitizeResultNameForValue(
      matchedValue?.[0]?.value,
      query,
      htmlTag,
    )?.toLowerCase() ?? null

  return futureSentence
}
